// Work Management (work-management-service) — native work definition +
// lifecycle: projects, work items, checklist, evidence, review. Distinct from
// workAssignmentService (field execution / presence). Backend JSON is
// snake_case; the API enforces tenant + org-scope server-side on every call.
import { gatewayApi } from '@/lib/hrApi';

const W = '/api/v1/work-items';
const P = '/api/v1/projects';

export type WorkStatus =
  | 'draft' | 'planned' | 'assigned' | 'started' | 'in_progress'
  | 'submitted_for_completion' | 'under_review' | 'completed'
  | 'on_hold' | 'changes_requested' | 'cancelled' | 'reopened';

/** How a parent with sub-items reaches completion (design §6): `manual` (the
 * owner submits whenever they judge the work done) or `require_subitems` (the
 * server refuses submission until every sub-item is completed or cancelled).
 * Children never complete the parent; the parent never auto-completes. */
export type CompletionPolicy = 'manual' | 'require_subitems';

/** Does finishing this work need a second pair of eyes (completion policy,
 * docs/work-management/13-completion-policy.md)? It is a property of the work
 * TYPE, not of the company, so it resolves item → template → tenant, most
 * specific first. `inherit` (the default) defers to the next level up. */
export type CompletionReview = 'inherit' | 'required' | 'not_required';
export const COMPLETION_REVIEWS: CompletionReview[] = ['inherit', 'required', 'not_required'];

// ---- Workload Intelligence + Work Health (Phase 1) ----
// Health describes the WORK, never the person: every value carries the reasons
// that produced it (`explain`) so the UI can always answer "Why?".
export type WorkHealth = 'ON_TRACK' | 'AT_RISK' | 'OVERDUE' | 'STALE' | 'BLOCKED' | 'AWAITING_REVIEW';
export interface WorkItemHealth {
  primary_health: WorkHealth;
  signals: string[];
  explain: string[];
}
/** Relative size for workload planning — not hours. `null` = not sized. */
export type EffortSize = 'XS' | 'S' | 'M' | 'L' | 'XL';
export const EFFORT_SIZES: EffortSize[] = ['XS', 'S', 'M', 'L', 'XL'];

export interface WorkItem {
  id: string;
  /** Set only on a REMOVED item — which only work:remove holders ever see
   *  (the Removed view / include_removed). */
  removed_at?: string | null;
  removed_by_name?: string | null;
  removal_reason_code?: WorkRemovalReason | null;
  removal_note?: string | null;
  removal_batch_id?: string | null;
  /** Effort recorded at submission (hours) — payroll's proposed-hours input. */
  effort_hours?: number | null;
  project_id?: string;
  parent_id?: string;
  type_key: string;
  /** individual (default) | team — team keeps ONE accountable owner; the
   * collaboration is contributors + individually-owned sub-items. */
  work_type?: string;
  title: string;
  description?: string;
  status: WorkStatus;
  owner_employee_id?: string;
  priority: string;
  planned_start_at?: string;
  planned_end_at?: string;
  actual_start_at?: string;
  submitted_at?: string;
  completed_at?: string;
  /** 0–100; the server averages the children for team items. */
  progress: number;
  source: string;
  /** Default `manual`; absent on older payloads. */
  completion_policy?: CompletionPolicy;
  /** This item's own override; `inherit` (default) defers to the template, then
   * to the tenant policy. */
  completion_review?: CompletionReview;
  /** The RESOLVED answer, computed server-side (item → template → tenant): true
   * when submitting hands the work to a reviewer, false when the owner completes
   * it themselves. NEVER re-derive the rule in the client — render this. Absent
   * on older backends, where review was always required. */
  review_required?: boolean;
  decision_note?: string;
  /** Creator (auth user id) — drives the "Created by me" tab server-side. */
  created_by?: string;
  /** Owner's display name, resolved server-side for list rows (Collaborating /
   * Team Work tabs). Absent on older payloads — fall back to the /names lookup. */
  owner_name?: string | null;
  /** Computed server-side on list rows and the composite detail; absent on
   * older backends or when the tenant has workload intelligence off. */
  health?: WorkItemHealth | null;
  effort_size?: EffortSize | null;
  created_at: string;
  updated_at: string;
}

export interface WorkChecklistItem {
  id: string; label: string; position: number; required: boolean;
  done: boolean; done_at?: string;
}
export interface EvidenceRequirement {
  id: string; req_key: string; label: string;
  phase: 'start' | 'completion'; kind: string; required: boolean;
}
export interface WorkEvidence {
  id: string; requirement_id?: string; phase: string; kind: string;
  caption?: string; note?: string; file_name?: string; content_type?: string;
  storage_key?: string; created_at: string;
}

// Work assets — files/links attached to a work item with a version chain. A
// version is an uploaded file OR an external link (provider detected server-side).
export type AssetPurpose = 'reference' | 'deliverable' | 'supporting';
export type AssetProvider =
  | 'kazipeople' | 'google_drive' | 'onedrive' | 'sharepoint' | 'dropbox' | 'figma' | 'adobe' | 'external';
export interface WorkAssetVersion {
  id: string; asset_id: string; version: number;
  type: 'uploaded_file' | 'external_link'; provider: AssetProvider;
  url?: string; file_name?: string; content_type?: string; size_bytes?: number;
  reason: string; added_by?: string; created_at: string;
}
export interface WorkAsset {
  id: string; work_item_id: string; purpose: AssetPurpose; name: string;
  description?: string; current_version: number; added_by?: string;
  created_at: string; updated_at: string; archived_at?: string;
  current?: WorkAssetVersion; versions?: WorkAssetVersion[];
}
export interface WorkActivity {
  id: string; actor_user_id?: string; actor_name?: string; verb: string; summary: string; occurred_at: string;
  /** Asset rows (asset_added / asset_version_added / asset_removed): which file —
   *  opened through the authorized download, never a stored URL. Absent on older rows. */
  asset_id?: string; asset_version_id?: string;
  /** The asset has since been removed from the item — no "Open". */
  asset_removed?: boolean;
  /** A comment posted WITH an upload: the assets it accompanies. */
  attachment_asset_ids?: string[];
}
export interface WorkExecutionLink { assignment_id: string; linked_at: string }
export interface SubitemRollup {
  total: number;
  done: number;
  /** Open sub-items at EVERY depth — what a cascading cancel would stop.
   *  Absent on older backends. */
  open_descendants?: number;
}
/** One row of the item's assignment history (design §6): who moved the
 * accountable owner, from whom, to whom, and why. Names are projected
 * server-side from the roster so the UI never resolves them. Newest first. */
export type AssignmentHistoryKind = 'assign' | 'reassign' | 'unassign' | 'subtask_assign' | 'subtask_reassign' | 'subtask_unassign';
export interface AssignmentHistoryEntry {
  id: string;
  kind: AssignmentHistoryKind;
  previous_employee_id?: string | null;
  /** The previous owner's auth user id — equal to changed_by_user_id when the
   *  owner handed the work back themselves. */
  previous_user_id?: string | null;
  previous_name?: string | null;
  /** null for an unassignment (kind unassign / subtask_unassign). */
  new_employee_id: string | null;
  new_name: string;
  changed_by_user_id: string;
  changed_by_name: string;
  reason?: string | null;
  occurred_at: string;
}
/** The caller's relationship to ONE work item, computed server-side (design
 * §6): owner vs collaborator decides which lifecycle affordances render; the
 * backend still enforces every action (403 + message on refusal). */
export interface WorkViewer {
  is_owner: boolean;
  /** Active contributor who is not the owner. */
  is_collaborator: boolean;
  /** Owner/creator may add collaborators without work:assign when the tenant
   * policy allows collaborators. */
  can_add_collaborators: boolean;
  can_review: boolean;
  /** Correction affordances, evaluated server-side with the SAME rule the
   *  action enforces (owner / creator / manager of the owner / work admin).
   *  Absent on older backends — callers fall back to their previous checks. */
  can_assign?: boolean;
  can_reassign?: boolean;
  can_cancel?: boolean;
  /** Remove the assignment (back to the unassigned queue). For the owner this
   *  is a hand-back, allowed only before they start the work. */
  can_unassign?: boolean;
  /** Admin (work:remove): take it out of every view — recoverable. */
  can_remove?: boolean;
  /** Admin (work:remove): bring a removed item back. */
  can_restore?: boolean;
  /** The caller may switch off the repeating series this occurrence came from. */
  can_stop_series?: boolean;
  /** The active recurring series this occurrence belongs to. */
  series_id?: string;
}
export interface WorkItemDetail {
  work_item: WorkItem;
  checklist: WorkChecklistItem[] | null;
  evidence_requirements: EvidenceRequirement[] | null;
  evidence: WorkEvidence[] | null;
  execution_links: WorkExecutionLink[] | null;
  activity: WorkActivity[] | null;
  subitems?: SubitemRollup;
  /** Same array as GET /work-items/{id}/history, embedded on the detail. */
  assignment_history?: AssignmentHistoryEntry[] | null;
  /** Absent on older backends — the UI falls back to owner_employee_id === me. */
  viewer?: WorkViewer;
}

/** Record that this work item is executed via a workassignment (reference-only:
 * execution never drives the work lifecycle — completion still needs evidence
 * + approval). */
/** Reverse execution-link lookup: which work item does this assignment
 * execute? 404 = unlinked (or outside the caller's reach). */
export const getWorkItemByAssignment = async (assignmentId: string): Promise<WorkItem | null> => {
  try {
    return (await gatewayApi.get<{ work_item: WorkItem }>(`${W}/by-assignment/${assignmentId}`)).data.work_item;
  } catch {
    return null;
  }
};

export const linkExecution = (workItemId: string, assignmentId: string) =>
  gatewayApi.post(`${W}/${workItemId}/execution-link`, { assignment_id: assignmentId });
export interface WorkProject {
  id: string; code?: string; name: string; description?: string; status: string;
  project_manager_employee_id?: string; priority: string; created_at: string;
}
export interface Page<T> {
  items: T[]; total: number; limit: number; offset: number;
  /** Batched sub-item rollups for the returned page, keyed by work-item id. */
  subitem_counts?: Record<string, SubitemRollup>;
}

export interface WorkListParams {
  status?: string;        // comma-separated
  parent_id?: string;
  project_id?: string;
  assignee?: 'me';
  /** Universal Work visibility tabs (design §7): created_by=me, collaborating=me
   * (active collaborator rows, not owner), scope=team (work:read population
   * minus self). The server resolves every one — the client never filters. */
  created_by?: 'me';
  collaborating?: 'me';
  scope?: 'team';
  /** With scope=team: which link to the team counts — owned by (default),
   *  created by, or a team member collaborates on it. Server-resolved. */
  relation?: 'owner' | 'creator' | 'collaborator';
  /** With scope=team: direct reports (default) or the whole reporting line. */
  reach?: 'subtree';
  queue?: 'review';
  /** The Removed view (work:remove only): removed items instead of live ones. */
  removed?: 'only';
  q?: string;             // title search (contains)
  sort?: 'updated' | 'planned';
  limit?: number;
  offset?: number;
}

export const listWorkItems = async (params: WorkListParams = {}): Promise<Page<WorkItem>> =>
  (await gatewayApi.get<Page<WorkItem>>(`${W}/`, { params })).data;

/** Count-per-status over the caller's visible set (honors q/scope, ignores status). */
export const workItemStatusCounts = async (params: WorkListParams = {}): Promise<Record<string, number>> =>
  (await workItemCountsWithRemoved(params)).counts;

/** Status counts plus, for work:remove holders, the Removed chip's count. The
 *  server returns it under the `removed` key; it is NOT a status, so it is split
 *  out here and never reaches an "All" total. */
export const workItemCountsWithRemoved = async (params: WorkListParams = {}): Promise<{ counts: Record<string, number>; removed: number | null }> => {
  const raw = (await gatewayApi.get<{ counts: Record<string, number> }>(`${W}/status-counts`, { params })).data.counts || {};
  const { removed, ...counts } = raw;
  return { counts, removed: typeof removed === 'number' ? removed : null };
};

/** includeRemoved: work:remove holders get a removed item (banner + Restore);
 *  anyone else gets 410 `work_item_removed` — "This work item was removed by …". */
export const getWorkItem = async (id: string, opts?: { includeRemoved?: boolean }): Promise<WorkItemDetail> =>
  (await gatewayApi.get<WorkItemDetail>(`${W}/${id}`, opts?.includeRemoved ? { params: { include_removed: 1 } } : undefined)).data;

export const listProjects = async (params: WorkListParams = {}): Promise<Page<WorkProject>> =>
  (await gatewayApi.get<Page<WorkProject>>(`${P}/`, { params })).data;

export const createProject = async (body: { name: string; description?: string; priority?: string }): Promise<WorkProject> =>
  (await gatewayApi.post<WorkProject>(`${P}/`, body)).data;

export interface ChecklistSpec { label: string; required: boolean }
export interface EvidenceSpec { req_key?: string; label: string; phase?: string; kind?: string; required: boolean }

/** Work template ("work type"): reusable tenant config — default checklist +
 * evidence pack + priority. Curated with work:manage; anyone with work:read
 * picks from the list at creation time. */
export interface RecurrenceRule {
  freq: 'daily' | 'weekly' | 'monthly';
  interval: number;
  weekdays?: number[];   // weekly: 0=Sun..6=Sat
  by_monthday?: number;  // monthly: 1..31 (0 = anchor day)
}
export interface WorkTemplate {
  id: string; name: string; description?: string;
  type_key: string; priority: string;
  checklist: ChecklistSpec[]; evidence: EvidenceSpec[];
  require_subitems_complete?: boolean;
  /** Whether work of this type needs a reviewer to complete it; `inherit`
   * (default) follows the company policy. */
  completion_review?: CompletionReview;
  active: boolean; created_at: string; updated_at: string;
  recurrence_rule?: RecurrenceRule | null;
  recurrence_active?: boolean;
  recurrence_anchor?: string | null;
  recurrence_tz?: string;
  default_planned_duration_minutes?: number | null;
  default_assignee_id?: string | null;
  default_project_id?: string | null;
  /** Employee ids added as collaborators on every spawned occurrence. */
  default_collaborator_ids?: string[] | null;
  /** Effort size every task created from this template starts with
   * (null/absent = not sized). Relative units, not hours. */
  default_effort_size?: EffortSize | null;
}

const T = '/api/v1/work-templates';

export const listWorkTemplates = async (includeInactive = false): Promise<WorkTemplate[]> =>
  (await gatewayApi.get<{ items: WorkTemplate[] }>(`${T}/`, {
    params: includeInactive ? { include_inactive: 'true' } : undefined,
  })).data.items || [];

export interface WorkTemplateBody {
  name: string; description?: string; priority?: string;
  checklist: ChecklistSpec[]; evidence: EvidenceSpec[];
  require_subitems_complete?: boolean; active?: boolean;
  /** inherit | required | not_required (default `inherit`). */
  completion_review?: CompletionReview;
  recurrence_rule?: RecurrenceRule | null;
  recurrence_active?: boolean;
  recurrence_anchor?: string | null;
  recurrence_tz?: string;
  default_planned_duration_minutes?: number | null;
  default_assignee_id?: string | null;
  default_project_id?: string | null;
  default_collaborator_ids?: string[];
  /** Send `null` to clear ("Not sized"). */
  default_effort_size?: EffortSize | null;
}
export const createWorkTemplate = async (body: WorkTemplateBody): Promise<WorkTemplate> =>
  (await gatewayApi.post<WorkTemplate>(`${T}/`, body)).data;
export const updateWorkTemplate = async (id: string, body: WorkTemplateBody): Promise<WorkTemplate> =>
  (await gatewayApi.put<WorkTemplate>(`${T}/${id}`, body)).data;
/** Switch a repeating series off — allowed for whoever set it up, or a work
 *  administrator. Occurrences already created are untouched. Idempotent. */
export const stopWorkSeries = async (templateId: string): Promise<WorkTemplate> =>
  (await gatewayApi.post<WorkTemplate>(`${T}/${templateId}/series/stop`, {})).data;

export interface CreateWorkItemBody {
  project_id?: string;
  /** Sub-item: the child inherits the parent's project + owner and is
   * auto-assigned server-side so it's immediately actionable. */
  parent_id?: string;
  owner_employee_id?: string;
  template_id?: string;
  goal_id?: string;
  title: string;
  description?: string;
  priority?: string;
  type_key?: string;
  work_type?: string; // individual (default) | team
  /** Planned schedule (when the work is meant to run) — ISO datetimes. */
  planned_start_at?: string;
  planned_end_at?: string;
  checklist?: Array<{ label: string; required: boolean }>;
  evidence_requirements?: EvidenceSpec[];
  /** Relative size for workload planning — not hours (null/absent = not sized). */
  effort_size?: EffortSize | null;
  /** Per-item override of "does finishing need review" (default `inherit`). */
  completion_review?: CompletionReview;
}
export const createWorkItem = async (body: CreateWorkItemBody): Promise<WorkItem> =>
  (await gatewayApi.post<WorkItem>(`${W}/`, body)).data;

/** Edit the definition fields (actor/creator/reviewer; blocked once submitted).
 * `completion_policy` rides the same authorization as the title. */
export const updateWorkItem = async (
  id: string,
  body: {
    title?: string; description?: string; priority?: string;
    planned_start_at?: string; planned_end_at?: string;
    completion_policy?: CompletionPolicy;
    completion_review?: CompletionReview;
    /** Send `null` to clear ("Not sized"). */
    effort_size?: EffortSize | null;
  },
): Promise<WorkItem> => (await gatewayApi.patch<WorkItem>(`${W}/${id}`, body)).data;

/** Duplicate a task's definition into a fresh draft ("Copy of …"). */
export const duplicateWorkItem = async (id: string): Promise<WorkItem> =>
  (await gatewayApi.post<WorkItem>(`${W}/${id}/duplicate`, {})).data;

/** "New work item + Repeat" → creates a recurring series (a recurring template
 * that auto-spawns tasks) and materializes any already-due first occurrence. */
export interface CreateRecurringSeriesBody {
  title: string;
  description?: string;
  priority?: string;
  type_key?: string;
  project_id?: string;
  owner_employee_id?: string;
  /** Collaborators every occurrence inherits (design §6 recurrence). */
  collaborator_ids?: string[];
  checklist?: Array<{ label: string; required: boolean }>;
  evidence_requirements?: EvidenceSpec[];
  recurrence_rule: RecurrenceRule;
  recurrence_anchor: string;
  recurrence_tz?: string;
  default_planned_duration_minutes?: number;
  /** Effort size applied to EVERY occurrence (relative units, not hours). */
  effort_size?: EffortSize | null;
}
export const createRecurringWorkItem = async (body: CreateRecurringSeriesBody): Promise<{ spawned: number }> =>
  (await gatewayApi.post<{ spawned: number }>(`${W}/recurring`, body)).data;

/** Discussion entry (activity verb "comment") — collaboration, distinct from evidence. */
export const addWorkComment = (id: string, text: string) =>
  gatewayApi.post(`${W}/${id}/comments`, { text });

/** INITIAL assignment only (draft / planned / reopened, no owner yet). An
 * already-owned item must go through `reassignWorkItem`. */
export const assignWorkItem = async (id: string, employeeId: string, userId?: string): Promise<WorkItem> =>
  (await gatewayApi.post<WorkItem>(`${W}/${id}/assign`, { employee_id: employeeId, user_id: userId })).data;

/** Move the accountable owner of an already-assigned item (status-preserving;
 * the backend writes history + activity + audit and notifies both owners).
 * 400 = tenant disabled reassignment / state forbids it; 403 = target outside
 * the caller's assignment scope — both carry a plain `message` for the toast. */
export interface ReassignWorkItemBody {
  employee_id: string;
  user_id?: string;
  reason?: string;
  /** Carry the open sub-items that still have the previous owner (the ones
   *  that inherited it). The server defaults to true when omitted. */
  move_subtasks?: boolean;
  /** The item's `updated_at` as last read — 409 if someone changed it since. */
  expected_updated_at?: string;
}
export const reassignWorkItem = async (id: string, body: ReassignWorkItemBody): Promise<WorkItem> =>
  (await gatewayApi.post<WorkItem>(`${W}/${id}/reassign`, {
    employee_id: body.employee_id,
    ...(body.user_id ? { user_id: body.user_id } : {}),
    ...(body.reason?.trim() ? { reason: body.reason.trim() } : {}),
    ...(body.move_subtasks !== undefined ? { move_subtasks: body.move_subtasks } : {}),
    ...(body.expected_updated_at ? { expected_updated_at: body.expected_updated_at } : {}),
  })).data;

/** Remove the accountable owner: the item returns to the unassigned queue as a
 *  draft (start cleared), keeping its checklist, evidence and history. Open
 *  sub-items that inherited the owner follow unless `move_subtasks` is false.
 *  Idempotent — an item with no owner comes back unchanged. */
export interface UnassignWorkItemBody {
  reason?: string;
  move_subtasks?: boolean;
  /** The item's `updated_at` as last read — 409 if someone changed it since. */
  expected_updated_at?: string;
}
export const unassignWorkItem = async (id: string, body: UnassignWorkItemBody = {}): Promise<WorkItem> =>
  (await gatewayApi.post<WorkItem>(`${W}/${id}/unassign`, {
    ...(body.reason?.trim() ? { reason: body.reason.trim() } : {}),
    ...(body.move_subtasks !== undefined ? { move_subtasks: body.move_subtasks } : {}),
    ...(body.expected_updated_at ? { expected_updated_at: body.expected_updated_at } : {}),
  })).data;

/** Why a work item is removed — codes in the server's order, 'mistake' first.
 *  Mirrors work-management-service internal/work/removal.go. Required. */
export const WORK_REMOVAL_REASONS = [
  { code: 'mistake', label: 'Created by mistake' },
  { code: 'duplicate', label: 'Duplicate' },
  { code: 'test_data', label: 'Test data' },
  { code: 'other', label: 'Other' },
] as const;
export type WorkRemovalReason = (typeof WORK_REMOVAL_REASONS)[number]['code'];

/** What remove / restore return: the item, how many items the batch held, and
 *  anything restore did on its own (an inactive owner removed, a stopped series). */
export interface WorkRemovalResult { work_item: WorkItem; count: number; notes: string[] }

/** Admin-only (work:remove): take an item — and its live sub-items, as one
 *  batch — out of every working and reporting view. Nothing is deleted; status
 *  is untouched; Restore brings it back. Idempotent. */
export const removeWorkItem = async (
  id: string,
  body: { reason_code: WorkRemovalReason; note?: string; expected_updated_at?: string },
): Promise<WorkRemovalResult> =>
  (await gatewayApi.post<WorkRemovalResult>(`${W}/${id}/remove`, {
    reason_code: body.reason_code,
    ...(body.note?.trim() ? { note: body.note.trim() } : {}),
    ...(body.expected_updated_at ? { expected_updated_at: body.expected_updated_at } : {}),
  })).data;

/** Admin-only: restore the batch an item was removed in. Refused (409) while
 *  its parent is removed. Idempotent. */
export const restoreWorkItem = async (id: string): Promise<WorkRemovalResult> =>
  (await gatewayApi.post<WorkRemovalResult>(`${W}/${id}/restore`, {})).data;

/** After "Discuss" resolves the item's conversation, ask the work service to put the
 * owner, collaborators and creator into it too (server resolves who they are). */
export const syncWorkDiscussion = async (id: string): Promise<{ user_ids: string[]; synced: number }> =>
  (await gatewayApi.post<{ user_ids: string[]; synced: number }>(`${W}/${id}/discussion/participants`, {})).data;

/** Assignment history for one item, newest first (also embedded on the detail). */
export const getWorkHistory = async (id: string): Promise<AssignmentHistoryEntry[]> =>
  (await gatewayApi.get<{ history: AssignmentHistoryEntry[] | null }>(`${W}/${id}/history`)).data.history ?? [];

// ---- Eligible assignees (design §5): server search + paging over the caller's
// assignment scope. Directory projection only — never a roster download. A
// caller without the capability gets 200 + reason, never a 403 the UI must
// translate; the picker renders a permission-aware empty state from `reason`.
export type EligiblePurpose = 'assign' | 'collaborate' | 'subtask';
export type EligibleReason = 'OK' | 'NO_CAPABILITY' | 'POLICY_DISABLED';
export interface EligiblePerson {
  employee_id: string;
  user_id?: string | null;
  display_name: string;
  avatar_url?: string | null;
  job_title?: string | null;
  department_name?: string | null;
  /** Present only with `with_workload=1` and the tenant feature on. */
  workload?: EligiblePersonWorkload | null;
}
export interface EligiblePersonWorkload {
  active: number;
  overdue: number;
  due_soon: number;
  band: WorkloadBand;
  confidence: WorkloadConfidence;
}
export type WorkloadBand = 'LOW' | 'NORMAL' | 'HIGH' | 'VERY_HIGH';
export type WorkloadConfidence = 'sized' | 'estimated' | 'counts_only';
export interface EligiblePage {
  items: EligiblePerson[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  reason: EligibleReason;
  /** Human scope label for the picker helper line ("direct reports", "Marketing department", "entire company"). */
  scope_summary: string;
}
export interface EligibleAssigneesParams {
  q?: string;
  page?: number;
  page_size?: number;
  purpose?: EligiblePurpose;
  work_item_id?: string;
  project_id?: string;
  /** 1 → each person carries a `workload` hint (when the feature is on). */
  with_workload?: 1;
}
export const listEligibleAssignees = async (params: EligibleAssigneesParams = {}): Promise<EligiblePage> => {
  const d = (await gatewayApi.get<Partial<EligiblePage>>(`${W}/eligible-assignees`, { params })).data || {};
  return {
    items: d.items || [],
    total: d.total ?? (d.items || []).length,
    page: d.page ?? params.page ?? 1,
    page_size: d.page_size ?? params.page_size ?? 20,
    has_more: !!d.has_more,
    reason: d.reason || 'OK',
    scope_summary: d.scope_summary || '',
  };
};

// ---- Tenant Work policy (design §8): the Access & Assignment toggles + the
// caller's resolved capabilities. GET is open to any authenticated user (the
// UI derives tabs/affordances from `me`); PUT needs work:manage.
export type EmployeeAssignmentScope = 'TENANT' | 'DEPARTMENT' | 'REPORTING_TREE' | 'CUSTOM';
export type ManagerAssignmentScope = 'DIRECT_REPORTS' | 'REPORTING_TREE' | 'DEPARTMENT' | 'BUSINESS_UNIT' | 'TENANT' | 'CUSTOM';
export interface WorkPolicy {
  work_available_to_all: boolean;
  employees_can_create: boolean;
  employees_can_assign: boolean;
  employee_assignment_scope: EmployeeAssignmentScope;
  managers_assign_outside_line: boolean;
  manager_assignment_scope: ManagerAssignmentScope;
  allow_cross_department_collab: boolean;
  allow_reassignment: boolean;
  allow_collaborators: boolean;
  allow_cross_team_subtasks: boolean;
  // Workload Intelligence + Work Health (Phase 1). Absent on older backends —
  // read with defaults, never assume.
  workload_enabled: boolean;
  workload_use_effort: boolean;
  workload_include_subitems: boolean;
  workload_include_recurring: boolean;
  workload_include_collaboration: boolean;
  stale_enabled: boolean;
  stale_after_days: number;
  /** How stale days are counted; absent on older backends → `calendar_days`.
   * `business_days` uses the company's rest days + public holidays (Time Off). */
  stale_basis?: 'calendar_days' | 'business_days';
  due_soon_days: number;
  /** Whose workload a manager may SEE. Auth's management scope is synchronized
   * from this value server-side (see `scope_sync`). */
  manager_visibility_scope: ManagerVisibilityScope;
  /** Relative weights, not hours. */
  effort_weights: Record<EffortSize, number>;
  /** Score thresholds where each band starts. */
  workload_bands: { normal: number; high: number; very_high: number };
  /** Phase 2 (capacity): when false the UI hides utilization even for people
   * with capacity configured. Absent on older backends → treated as true. */
  workload_show_utilization?: boolean;
  /** Completion policy (13-completion-policy.md). The COMPANY DEFAULT for
   * "finishing needs review" — a work type or a single item may override it.
   * Absent on older backends → treat as on (today's behaviour). */
  completion_requires_review?: boolean;
  /** When the last sub-item closes, move the parent as far as its own policy and
   * gate already allow. Absent on older backends → treat as on. */
  parent_auto_close?: boolean;
  /** Must work somebody assigned to THEMSELVES still go to a reviewer? Default
   * OFF: nobody may review their own work, so leaving it on means self-assigned
   * work needs a second person who can approve it — which is how items used to
   * get stranded. Absent on older backends → treat as off. */
  self_assigned_needs_review?: boolean;
}
export type ManagerVisibilityScope = 'DIRECT_REPORTS' | 'REPORTING_TREE' | 'DEPARTMENT' | 'TENANT';
/** Result of the server-side mirror of `manager_visibility_scope` into auth's
 * management scope policy. `failed` carries the reason; `resyncWorkPolicy`
 * retries it. */
export interface ScopeSync { status: 'ok' | 'failed' | 'pending'; at?: string; error?: string }
export interface WorkPolicyMe {
  can_create: boolean;
  can_assign: boolean;
  can_review: boolean;
  can_manage: boolean;
  /** Resolved reach; `groups` when the caller assigns through assignment groups
   * (scope_summary "your assignment groups"). */
  assignment_scope: string;
  scope_summary: string;
  /** Workload tab: Team/Departments render only when true (self is always visible). */
  can_view_workload?: boolean;
  workload_scope?: string;
  workload_scope_summary?: string;
}
export interface WorkPolicyView { policy: WorkPolicy; me: WorkPolicyMe; scope_sync?: ScopeSync | null }
const POLICY = '/api/v1/work/policy';
export const getWorkPolicy = async (): Promise<WorkPolicyView> =>
  (await gatewayApi.get<WorkPolicyView>(POLICY)).data;
export const putWorkPolicy = async (policy: WorkPolicy): Promise<WorkPolicyView> =>
  (await gatewayApi.put<WorkPolicyView>(POLICY, { policy })).data;
/** Re-run the auth scope mirror for the saved policy (after a `failed` sync). */
export const resyncWorkPolicy = async (): Promise<ScopeSync> =>
  (await gatewayApi.post<{ scope_sync: ScopeSync }>(`${POLICY}/resync`, {})).data.scope_sync;

// ---- Assignment groups (Universal Work Phase 5): hand-picked groups that
// apply when the assignment scope is Custom group. Assigners in a group can
// assign work to every member of that group. All routes need work:manage.
export type WorkGroupRole = 'assigner' | 'member';
/** What a group's assigners may see about its members beyond assigning:
 * nothing, the members' workload (counts + band), or their work items. */
export type WorkGroupVisibility = 'none' | 'workload' | 'work';
export const WORK_GROUP_VISIBILITIES: WorkGroupVisibility[] = ['none', 'workload', 'work'];
export interface WorkGroup {
  id: string;
  name: string;
  description?: string | null;
  active: boolean;
  /** Absent on older backends (treated as `none`). */
  visibility_level?: WorkGroupVisibility;
  member_count: number;
  assigner_count: number;
  created_at: string;
  updated_at: string;
}
export interface WorkGroupMember {
  employee_id: string;
  user_id?: string | null;
  role: WorkGroupRole;
  display_name: string;
  job_title?: string | null;
  department_name?: string | null;
}
export interface WorkGroupDetail { group: WorkGroup; members: WorkGroupMember[] }
export interface WorkGroupMemberInput { employee_id: string; role: WorkGroupRole }
const G = '/api/v1/work/groups';
export const listWorkGroups = async (): Promise<WorkGroup[]> =>
  (await gatewayApi.get<{ groups?: WorkGroup[] }>(G)).data?.groups || [];
export const createWorkGroup = async (body: { name: string; description?: string; visibility_level?: WorkGroupVisibility }): Promise<WorkGroup> =>
  (await gatewayApi.post<WorkGroup>(G, body)).data;
export const getWorkGroup = async (id: string): Promise<WorkGroupDetail> => {
  const d = (await gatewayApi.get<Partial<WorkGroupDetail>>(`${G}/${id}`)).data || {};
  return { group: d.group as WorkGroup, members: d.members || [] };
};
export const updateWorkGroup = async (
  id: string, body: { name?: string; description?: string; active?: boolean; visibility_level?: WorkGroupVisibility },
): Promise<WorkGroup> => (await gatewayApi.put<WorkGroup>(`${G}/${id}`, body)).data;
export const deleteWorkGroup = (id: string) => gatewayApi.delete(`${G}/${id}`);
/** Replaces the whole member list; returns the detail shape. */
export const putWorkGroupMembers = async (id: string, members: WorkGroupMemberInput[]): Promise<WorkGroupDetail> => {
  const d = (await gatewayApi.put<Partial<WorkGroupDetail>>(`${G}/${id}/members`, { members })).data || {};
  return { group: d.group as WorkGroup, members: d.members || [] };
};

// ---- Team work: contributors + derived contribution ----
export interface WorkContributor { employee_id: string; user_id?: string; name?: string | null; assigned_at: string }
export interface WorkContribution {
  employee_id: string; user_id?: string; name?: string | null; is_owner: boolean;
  subtasks_total: number; subtasks_completed: number;
  effort_hours: number; evidence_count: number;
}
export interface WorkTeamView {
  work_item_id: string;
  work_type: string;
  owner_employee_id?: string;
  progress: number;
  contributors: WorkContributor[] | null;
  subtasks: WorkItem[] | null;
  contributions: WorkContribution[] | null;
}
export const getWorkTeam = async (id: string): Promise<WorkTeamView> =>
  (await gatewayApi.get<WorkTeamView>(`${W}/${id}/team`)).data;
export const addWorkContributor = async (id: string, employeeId: string): Promise<WorkItem> =>
  (await gatewayApi.post<WorkItem>(`${W}/${id}/contributors`, { employee_id: employeeId })).data;
export const removeWorkContributor = async (id: string, employeeId: string): Promise<WorkItem> =>
  (await gatewayApi.delete<WorkItem>(`${W}/${id}/contributors/${employeeId}`)).data;

// Lifecycle transitions — the backend state machine enforces from-states, the
// completion gate (submit) and separation of duties (approve/request-changes);
// surface its message verbatim on failure (backend-error-surfacing rule).
const transition = (action: string) => async (id: string, note?: string): Promise<WorkItem> =>
  (await gatewayApi.post<WorkItem>(`${W}/${id}/${action}`, note ? { note } : {})).data;

export const startWork = transition('start');
/** Submit for completion; optional effort hours feed payroll's PROPOSED hours
 *  staging (human-approved there — never silently payable). */
export const submitWork = async (id: string, effortHours?: number): Promise<WorkItem> =>
  (await gatewayApi.post<WorkItem>(`${W}/${id}/submit`, effortHours && effortHours > 0 ? { effort_hours: effortHours } : {})).data;
export const approveWork = transition('approve');
export const requestChanges = transition('request-changes');
export const holdWork = transition('hold');
/** Cancellation reasons, in the order the picker offers them. Mirrors
 *  work-management-service/internal/work/cancelreason.go — 'mistake' first
 *  because it is the commonest honest reason, and 'merged' kept apart from
 *  'not_needed' because work folded into another item is still being done. */
export const WORK_CANCEL_REASONS = [
  { code: 'mistake', label: 'Created by mistake / duplicate' },
  { code: 'not_needed', label: 'No longer needed' },
  { code: 'merged', label: 'Merged into another item' },
  { code: 'out_of_scope', label: 'Out of scope / not ours' },
  { code: 'other', label: 'Other' },
] as const;

export type WorkCancelReason = (typeof WORK_CANCEL_REASONS)[number]['code'];

/** Cancel a work item. The reason is optional on the wire — the server accepts
 *  a bare cancel, as it always did — but 'other' must carry a note. */
export const cancelWork = async (
  id: string,
  opts?: {
    reason?: WorkCancelReason;
    note?: string;
    /** Also cancel the open sub-items (every depth), with the same reason. */
    cascade?: boolean;
    /** The item's `updated_at` as last read — 409 if someone changed it since. */
    expectedUpdatedAt?: string;
  },
): Promise<WorkItem> =>
  (await gatewayApi.post<WorkItem>(`${W}/${id}/cancel`, {
    ...(opts?.reason ? { cancel_reason: opts.reason } : {}),
    ...(opts?.note ? { note: opts.note } : {}),
    ...(opts?.cascade ? { cascade: true } : {}),
    ...(opts?.expectedUpdatedAt ? { expected_updated_at: opts.expectedUpdatedAt } : {}),
  })).data;
export const reopenWork = transition('reopen');

export const toggleChecklist = (workItemId: string, itemId: string, done: boolean) =>
  gatewayApi.post(`${W}/${workItemId}/checklist/${itemId}/toggle`, { done });

export const addNoteEvidence = async (
  workItemId: string,
  body: { requirement_key?: string; note: string; phase?: string },
): Promise<WorkEvidence> =>
  (await gatewayApi.post<WorkEvidence>(`${W}/${workItemId}/evidence`, { kind: 'note', ...body })).data;

/** File evidence: multipart upload — the backend content-sniffs the MIME,
 * stores the bytes in the document-platform object store, and records the
 * reference (kind photo/document inferred server-side). 10 MB cap. */
export const uploadEvidence = async (
  workItemId: string,
  file: File,
  opts: { requirement_key?: string; caption?: string; phase?: string } = {},
): Promise<WorkEvidence> => {
  const fd = new FormData();
  fd.append('file', file);
  if (opts.requirement_key) fd.append('requirement_key', opts.requirement_key);
  if (opts.caption) fd.append('caption', opts.caption);
  if (opts.phase) fd.append('phase', opts.phase);
  return (await gatewayApi.post<WorkEvidence>(`${W}/${workItemId}/evidence/upload`, fd)).data;
};

/** Open a file evidence: deployed backends return a presigned URL (JSON);
 * the dev-disk fallback streams the bytes — handle both via a blob request. */
export const openEvidence = async (workItemId: string, evidenceId: string, fileName?: string): Promise<void> => {
  const res = await gatewayApi.get(`${W}/${workItemId}/evidence/${evidenceId}/download`, { responseType: 'blob' });
  const blob: Blob = res.data;
  if (blob.type.includes('application/json')) {
    const parsed = JSON.parse(await blob.text());
    if (parsed?.url) { window.open(parsed.url, '_blank', 'noopener'); return; }
    throw new Error('no download url');
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'evidence';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
};

// ---- Work assets (files + external links, versioned) ----

const assetsUrl = (workItemId: string) => `${W}/${workItemId}/assets`;

export const listWorkAssets = async (workItemId: string): Promise<WorkAsset[]> =>
  (await gatewayApi.get<{ assets: WorkAsset[] | null }>(assetsUrl(workItemId))).data.assets ?? [];

/** Attach an EXTERNAL link (Drive/Figma/…): stores the reference only — bytes
 * stay with the provider. The backend validates the URL + detects the provider. */
export const addWorkAssetLink = async (
  workItemId: string,
  body: { name: string; url: string; description?: string; purpose?: AssetPurpose },
): Promise<WorkAsset> => (await gatewayApi.post<WorkAsset>(`${assetsUrl(workItemId)}/link`, body)).data;

/** Upload a MANAGED file (25 MB cap — heavy assets should use a link instead). */
export const uploadWorkAsset = async (
  workItemId: string,
  file: File,
  opts: { name?: string; description?: string; purpose?: AssetPurpose; notify?: boolean } = {},
): Promise<WorkAsset> => {
  const fd = new FormData();
  fd.append('file', file);
  if (opts.name) fd.append('name', opts.name);
  if (opts.description) fd.append('description', opts.description);
  if (opts.purpose) fd.append('purpose', opts.purpose);
  // false: the file is part of a brand-new item, not news about it — no notice.
  if (opts.notify === false) fd.append('notify', 'false');
  return (await gatewayApi.post<WorkAsset>(`${assetsUrl(workItemId)}/upload`, fd)).data;
};

/** Attach several files in ONE operation, optionally with a note (posted as a
 *  Discussion comment). Participants get one grouped notification. Pass the same
 *  clientUploadId when retrying a failed submit — the server then returns the
 *  first attempt's assets instead of creating duplicates. */
export const uploadWorkAttachments = async (
  workItemId: string,
  files: File[],
  opts: { purpose?: AssetPurpose; message?: string; clientUploadId?: string } = {},
): Promise<{ batch_id: string; assets: WorkAsset[]; replayed?: boolean }> => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  if (opts.purpose) fd.append('purpose', opts.purpose);
  if (opts.message?.trim()) fd.append('message', opts.message.trim());
  if (opts.clientUploadId) fd.append('client_upload_id', opts.clientUploadId);
  return (await gatewayApi.post(`${assetsUrl(workItemId)}/upload-batch`, fd, { timeout: 180000 })).data;
};

/** Add a new VERSION (link) — history is never overwritten; this becomes current. */
export const addWorkAssetLinkVersion = async (
  workItemId: string, assetId: string, body: { url: string; reason?: string },
): Promise<WorkAsset> =>
  (await gatewayApi.post<WorkAsset>(`${assetsUrl(workItemId)}/${assetId}/versions/link`, body)).data;

/** Add a new VERSION (file). */
export const addWorkAssetFileVersion = async (
  workItemId: string, assetId: string, file: File, reason?: string,
): Promise<WorkAsset> => {
  const fd = new FormData();
  fd.append('file', file);
  if (reason) fd.append('reason', reason);
  return (await gatewayApi.post<WorkAsset>(`${assetsUrl(workItemId)}/${assetId}/versions/upload`, fd)).data;
};

export const updateWorkAsset = async (
  workItemId: string, assetId: string,
  body: { name?: string; description?: string; purpose?: AssetPurpose },
): Promise<WorkAsset> =>
  (await gatewayApi.patch<WorkAsset>(`${assetsUrl(workItemId)}/${assetId}`, body)).data;

export const archiveWorkAsset = (workItemId: string, assetId: string) =>
  gatewayApi.post(`${assetsUrl(workItemId)}/${assetId}/archive`, {});

/** Download a MANAGED file version (presigned URL or dev-disk blob stream). */
export const downloadWorkAssetVersion = async (
  workItemId: string, assetId: string, versionId: string, fileName?: string,
): Promise<void> => {
  const res = await gatewayApi.get(
    `${assetsUrl(workItemId)}/${assetId}/versions/${versionId}/download`, { responseType: 'blob' });
  const blob: Blob = res.data;
  if (blob.type.includes('application/json')) {
    const parsed = JSON.parse(await blob.text());
    if (parsed?.url) { window.open(parsed.url, '_blank', 'noopener'); return; }
    throw new Error('no download url');
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'file';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
};

/** Request an external acknowledgement: returns the single-use token ONCE —
 * build the shareable link as `${origin}/ack/${token}`. */
export const createAckInvite = async (
  workItemId: string,
  body: { name: string; email?: string; requirement_key?: string },
): Promise<{ token: string }> =>
  (await gatewayApi.post<{ token: string }>(`${W}/${workItemId}/ack-invites`, body)).data;
